import React, { useState, useEffect } from 'react';
import { Box, Stack, Typography, Avatar, Button } from '@mui/material';
import PlayerStatPill from './PlayerStatPill';
import palette from '../assets/sass/palette.scss';
import { doc, getDoc } from 'firebase/firestore';
import { useFirebase } from '../contexts/FirebaseContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Custom PlayerInfo component for match results
const MatchPlayerInfo = ({ player, index, photoUrl, winner, size }) => {
  const getUserInitial = (player) => {
    const userName = player?.userName || player?.username || '';
    return userName?.charAt(0)?.toUpperCase();
  };

  const initial = getUserInitial(player);

  return (
    <Box sx={{ textAlign: 'center' }}>
      <Box
        sx={{
          borderRadius: '50%',
          width: size.width,
          height: size.height,
          mx: 'auto',
          mb: 1,
          position: 'relative',
        }}
      >
        {winner && (
          <Box
            sx={{
              position: 'absolute',
              top: -6,
              right: -6,
              width: '24px',
              height: '24px',
              zIndex: 2,
              pointerEvents: 'none',
            }}
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 25 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <circle
                cx="12"
                cy="12"
                r="11"
                transform="matrix(-1 0 0 1 24.9707 0)"
                fill="#C8B359"
                stroke="white"
                strokeWidth="2"
              />
              <path
                d="M8.06967 9.05582H9.04924C9.02203 8.81093 9.02203 8.6522 9.02203 8.62499V8.1488H7.78849C7.58895 8.1488 7.39848 8.23497 7.27149 8.38916C7.13998 8.54789 7.08555 8.74743 7.12184 8.94698C7.27603 9.79957 7.96536 12.3846 10.573 12.8789C10.2601 12.4707 10.0152 12.058 9.81567 11.6544C8.70458 11.0286 8.25107 9.82678 8.06967 9.05582Z"
                fill="white"
              />
              <path
                d="M18.6398 8.38916C18.5128 8.23497 18.3223 8.1488 18.1228 8.1488H16.8892V8.62499C16.8892 8.65673 16.8892 8.81093 16.862 9.05582H17.8416C17.6647 9.82678 17.2067 11.0331 16.1001 11.6544C15.9006 12.058 15.6557 12.4707 15.3428 12.8789C17.9504 12.3846 18.6398 9.79504 18.794 8.94244C18.8303 8.7429 18.7758 8.53882 18.6443 8.38463L18.6398 8.38916Z"
                fill="white"
              />
              <path d="M15.1934 16.9883H10.7173V17.8953H15.1934V16.9883Z" fill="white" />
              <path
                d="M15.979 8.62544V7.81366H9.9292V8.62544C9.9292 8.75242 9.97908 11.6005 12.5097 13.5369V16.0902H13.3985V13.5369C15.9291 11.6005 15.979 8.75242 15.979 8.62544Z"
                fill="white"
              />
            </svg>
          </Box>
        )}
        {photoUrl || player?.photoURL ? (
          <Avatar
            alt={player.userName || player.userId}
            src={photoUrl ? photoUrl : player.photoUrl}
            sx={{ width: size.width, height: size.height, boxShadow: 2 }}
          />
        ) : (
          <Box
            sx={{
              width: size.width,
              height: size.height,
              borderRadius: '50%',
              bgcolor: index % 2 === 0 ? palette.paleGreen : palette.palePurple,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography
              variant="factorA"
              sx={{ fontSize: '16px', color: '#4C4C4C', fontWeight: 700 }}
            >
              {initial ? initial : player?.initial}
            </Typography>
          </Box>
        )}
      </Box>
      <Typography
        sx={{
          fontFamily: '"FactorA Medium", arial, sans-serif',
          fontWeight: 700,
          fontSize: size.fontSize || '12px',
          textAlign: 'center',
          mb: 0.5,
        }}
      >
        {player?.userName || player?.username || player?.userId}
      </Typography>
      <PlayerStatPill waglScore={50} handicap={player.handicap || 0} />
    </Box>
  );
};

const GameMatchResult = ({
  match,
  users = [],
  gameId = null,
  gameData = null,
  hideCTA = false,
  isOrganiser = false,
}) => {
  console.log('hideCTA', hideCTA);
  const { db } = useFirebase();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [fetchedUsers, setFetchedUsers] = useState([]);
  // Determine if current user is the organizer of this game
  // Use passed isOrganiser prop if available, otherwise try to detect from gameData/match
  const isOrganizer = isOrganiser ||
    gameData?.createdBy?.id === currentUser?.uid ||
    gameData?.createdBy === currentUser?.uid ||
    match?.createdBy?.id === currentUser?.uid ||
    match?.createdBy === currentUser?.uid;
  
  console.log('GameMatchResult Debug:', {
    isOrganizer,
    currentUserId: currentUser?.uid,
    gameCreatedBy: gameData?.createdBy,
    matchCreatedBy: match?.createdBy,
    matchStatus: match?.status,
    matchFormat: match?.format,
    hideCTA,
    matchPlayers: match?.players,
    matchObject: match
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (users && users.length > 0) {
        setFetchedUsers(users);
        return;
      }

      if (!match || !match.players) return;

      try {
        const userPromises = match.players.map(async (player) => {
          const userId = player.uid || player.user;
          if (!userId) return null;

          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          return userDoc.exists ? { uid: userId, ...userDoc.data() } : null;
        });

        const userResults = await Promise.all(userPromises);
        setFetchedUsers(userResults.filter((user) => user !== null));
      } catch (error) {
        console.error('Error fetching users:', error);
      }
    };

    fetchUsers();
  }, [match, users, db]);

  if (!match || !match.players) {
    return null;
  }

  const usersToUse = users.length > 0 ? users : fetchedUsers;

  const getUserPhotoURL = (player) => {
    return player.photoURL;
  };

  // Helper function to find user details with improved matching logic
  const findUserDetails = (player) => {
    const playerId = player?.uid || player?.user;
    if (!playerId) return null;

    return usersToUse.find(
      (user) => {
        // Match on uid field
        if (user?.uid === playerId) return true;
        // Match on user field (common in invitations)
        if (user?.user === playerId) return true;
        // Also check reverse matching
        const userId = user?.uid || user?.user;
        if (userId === playerId) return true;
        return false;
      }
    );
  };

  const isMatchPlay = match.format === 'match';
  const isStrokePlay = match.format === 'stroke';

  // Check if the match has actual score data, not just status or default zeros
  // For match play: check if any player has didWin = true OR has non-zero scores
  // For stroke play: check if any player has non-zero scores
  const matchScored = match.players?.some(player => {
    const hasWinner = player.didWin === true || player.win === true;
    const hasGrossScore = player.grossScore && player.grossScore > 0;
    const hasNetScore = player.netScore && player.netScore > 0;
    return hasWinner || hasGrossScore || hasNetScore;
  });
  
  console.log('GameMatchResult CTA Logic:', {
    matchScored,
    isOrganizer,
    hideCTA,
    shouldShowButton: !matchScored && isOrganizer && !hideCTA
  });

  const handleEnterResults = (e) => {
    // Stop propagation to prevent parent card click
    e.stopPropagation();
    if (gameId) {
      navigate(`/games/${gameId}`);
    }
  };

  // For stroke play, display all players horizontally
  if (isStrokePlay) {
    return (
      <Box sx={{ px: 1, py: 1, minHeight: '160px' }}>
        <Stack
          direction="row"
          spacing={0}
          sx={{ justifyContent: 'space-between', flexWrap: 'nowrap', width: '100%' }}
        >
          {match.players.map((player, index) => {
            const userDetails = findUserDetails(player);

            const playerWithDetails = {
              ...player,
              userName:
                userDetails?.userName ||
                userDetails?.username ||
                player?.userName ||
                player?.username ||
                '',
              photoURL: userDetails?.photoURL || userDetails?.profileImage || player?.photoURL || null,
            };

            return (
              <React.Fragment key={player.uid || player.user || index}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <MatchPlayerInfo
                    player={playerWithDetails}
                    index={index}
                    photoUrl={getUserPhotoURL(playerWithDetails)}
                    winner={player.win || player.didWin}
                    size={{ width: 35, height: 35 }}
                  />
                  {matchScored && (
                    <Typography
                      sx={{
                        fontFamily: '"FactorA Medium", arial, sans-serif',
                        fontWeight: 700,
                        fontSize: '14px',
                        textAlign: 'center',
                        mt: 0.5,
                        color: player.win || player.didWin ? '#C8B359' : '#4C4C4C',
                      }}
                    >
                      {player.grossScore || player.netScore || 0}
                    </Typography>
                  )}
                </Box>
                {index < match.players.length - 1 && (
                  <Box
                    sx={{
                      width: '1px',
                      height: '60px',
                      backgroundColor: '#E5E7EB',
                      mx: 0.5,
                    }}
                  />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
        {/* Show scores, Enter Results button, or Result Pending */}
        {matchScored ? (
          // Scores already exist - don't show anything extra for stroke play
          // The scores are already displayed above next to each player
          <Box sx={{ height: '20px' }} />
        ) : !matchScored && isOrganizer && !hideCTA ? (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleEnterResults}
              sx={{
                backgroundColor: palette.mediumPurple,
                color: 'white',
                fontWeight: 600,
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: palette.darkPurple,
                },
              }}
            >
              Enter Score
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              textAlign: 'center',
              mt: 0,
              pt: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" sx={{ fontSize: '14px', fontWeight: 600 }}>
              Result
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontSize: '14px', color: '#FFC107', fontWeight: 600, paddingLeft: 1 }}
            >
              Pending
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // For match play, display teams with match result
  if (isMatchPlay) {
    const groupA = match.players.filter((player) => player.group === 'A');
    const groupB = match.players.filter((player) => player.group === 'B');

    const groupAScore = groupA[0]?.grossScore || 0;
    const groupBScore = groupB[0]?.grossScore || 0;
    const groupAWon = groupA[0]?.didWin;
    const groupBWon = groupB[0]?.didWin;

    // Calculate match result only if scores exist
    let matchResult = '';
    if (matchScored) {
      if (groupAScore === 0 && groupBScore === 0) {
        matchResult = 'Halved';
      } else if (groupAWon) {
        if (groupAScore === 1 && groupBScore === 0) {
          matchResult = '1 Up';
        } else {
          matchResult = `${groupAScore}&${groupBScore}`;
        }
      } else if (groupBWon) {
        if (groupBScore === 1 && groupAScore === 0) {
          matchResult = '1 Up';
        } else {
          matchResult = `${groupBScore}&${groupAScore}`;
        }
      }
    }

    return (
      <Box sx={{ px: 1, py: 1, minHeight: '160px' }}>
        <Box
          sx={{
            py: 1.5,
            px: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {/* Group A */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            {groupA.map((player, index) => {
              const userDetails = findUserDetails(player);

              const playerWithDetails = {
                ...player,
                userName:
                  userDetails?.userName ||
                  userDetails?.username ||
                  player?.userName ||
                  player?.username ||
                  '',
                photoURL: userDetails?.photoURL || userDetails?.profileImage || player?.photoURL || null,
              };

              return (
                <Box key={player.uid || player.user || index} sx={{ flex: 1 }}>
                  <MatchPlayerInfo
                    player={playerWithDetails}
                    index={index}
                    photoUrl={getUserPhotoURL(playerWithDetails)}
                    winner={groupAWon}
                    size={{ width: 35, height: 35 }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Vertical Divider */}
          <Box
            sx={{
              width: '1px',
              height: '50px',
              backgroundColor: '#E5E7EB',
              mx: 0.5,
            }}
          />

          {/* Group B */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1 }}>
            {groupB.map((player, index) => {
              const userDetails = findUserDetails(player);

              const playerWithDetails = {
                ...player,
                userName:
                  userDetails?.userName ||
                  userDetails?.username ||
                  player?.userName ||
                  player?.username ||
                  '',
                photoURL: userDetails?.photoURL || userDetails?.profileImage || player?.photoURL || null,
              };

              return (
                <Box key={player.uid || player.user || index} sx={{ flex: 1 }}>
                  <MatchPlayerInfo
                    player={playerWithDetails}
                    index={index}
                    photoUrl={getUserPhotoURL(playerWithDetails)}
                    winner={groupBWon}
                    size={{ width: 35, height: 35 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Match Result or Enter Results button */}
        <Box
          sx={{
            textAlign: 'center',
            mt: !isOrganizer ? 1 : 0.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          {matchScored ? (
            <>
              <Typography
                variant="body2"
                sx={{ fontSize: '14px', color: 'black', fontWeight: 600 }}
              >
                Result
              </Typography>
              <Typography variant="h6" fontWeight="bold" sx={{ fontSize: '16px' }}>
                {matchResult}
              </Typography>
            </>
          ) : isOrganizer && !hideCTA ? (
            <Button
              variant="contained"
              onClick={handleEnterResults}
              sx={{
                backgroundColor: palette.mediumPurple,
                color: 'white',
                fontWeight: 600,
                px: 3,
                py: 1,
                '&:hover': {
                  backgroundColor: palette.darkPurple,
                },
              }}
            >
              Enter Score
            </Button>
          ) : (
            <Typography variant="body2" sx={{ fontSize: '14px', color: 'black', fontWeight: 600 }}>
              Result
              <span style={{ color: '#FFC107', fontWeight: 600 }}> Pending</span>
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  return null;
};

export default GameMatchResult;
